import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Sparkles, Loader2, Play, Pause, Download, RotateCw, Radio, CheckCircle2, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveAudio = (u) => !u ? "" : (u.startsWith("http") ? u : `${BACKEND_URL}${u}`);

const VIBES = [
  { id: "warm",  label: "Warm",  desc: "Inspirational, TED-talk vibe" },
  { id: "hype",  label: "Hype",  desc: "Club MC, hype-man energy" },
  { id: "chill", label: "Chill", desc: "Late-night radio, conversational" },
  { id: "bold",  label: "Bold",  desc: "Rebellious, disruptive" },
];

export default function ProduceWizard() {
  const { token } = useParams();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState("");
  const [answers, setAnswers] = useState({
    business_name: "", audience: "", why: "", tagline: "", cta: "", vibe: "warm", language: "english", host_gender: "", guest_gender: "", show_name: "", guest_name: "", guest_title: "",
  });
  const [languages, setLanguages] = useState([{ key: "english", label: "English" }]);
  const [submitting, setSubmitting] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    // Populate the language dropdown from the backend so we don't ship a
    // duplicate list on the frontend that could drift from Claude's supported set.
    (async () => {
      try {
        const { data } = await api.get("/produce/languages");
        if (Array.isArray(data) && data.length) setLanguages(data);
      } catch (_) { /* silent — English default is fine */ }
    })();
  }, []);

  const fetchOrder = async () => {
    try {
      const { data } = await api.get(`/produce/order/${token}`);
      setOrder(data);
      setErr("");
      if (data.business_name && !answers.business_name) {
        setAnswers((a) => ({ ...a, business_name: data.business_name }));
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Order not found.");
    }
  };

  useEffect(() => {
    fetchOrder();
    // Apply saved template defaults if present
    try {
      const raw = sessionStorage.getItem(`fliboe_template_${token}`);
      if (raw) {
        const t = JSON.parse(raw);
        if (t?.defaults) {
          setAnswers((a) => ({ ...a, ...t.defaults }));
        }
      }
    } catch (_) {}
    // Poll while we wait for payment confirmation or generation
    const i = setInterval(() => {
      setOrder((cur) => {
        if (cur && (cur.status === "ready" || cur.status === "error" || cur.payment_status !== "paid" && cur.status === "awaiting_payment")) {
          // keep polling for awaiting_payment; stop only on ready/error
          if (cur.status === "ready" || cur.status === "error") {
            return cur;
          }
        }
        fetchOrder();
        return cur;
      });
    }, 3000);
    return () => clearInterval(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const submit = async () => {
    setErr("");
    for (const k of ["show_name", "guest_name", "guest_title", "audience", "why", "tagline", "cta"]) {
      if (!answers[k]?.trim()) { setErr("Fill in every field — keep it short, like one sentence each."); return; }
    }
    if (!answers.host_gender || !answers.guest_gender) {
      setErr("Pick a voice for both the host and the guest.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/produce/answers/${token}`, answers);
      await fetchOrder();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not start generation.");
    }
    setSubmitting(false);
  };

  const regenerate = async () => {
    if (!window.confirm("Regenerate the show with the same answers? You have a limited number of free re-tries.")) return;
    setSubmitting(true);
    try {
      await api.post(`/produce/regenerate/${token}`);
      await fetchOrder();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not regenerate.");
    }
    setSubmitting(false);
  };

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) { el.pause(); setPlaying(false); }
    else { el.play(); setPlaying(true); }
  };

  if (!order) {
    return (
      <main className="max-w-xl mx-auto px-5 py-20 text-center">
        {err ? <div className="text-red-400">{err}</div> : (
          <div className="text-slate-500 fl-mono text-xs uppercase tracking-widest flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading your order…
          </div>
        )}
      </main>
    );
  }

  const isPaid = order.payment_status === "paid" || order.payment_status === "waived";

  if (!isPaid) {
    return (
      <main className="max-w-xl mx-auto px-5 py-20" data-testid="produce-wizard-waiting">
        <div className="fl-card p-6 flex items-start gap-3">
          <Loader2 className="w-6 h-6 text-emerald-500 animate-spin shrink-0 mt-1" />
          <div>
            <div className="text-white font-medium">Waiting for payment confirmation…</div>
            <div className="text-xs text-slate-400 mt-1">This usually takes a few seconds. Don't close the tab.</div>
          </div>
        </div>
      </main>
    );
  }

  if (order.status === "ready") {
    return (
      <main className="max-w-2xl mx-auto px-5 py-14" data-testid="produce-result">
        <div className="fl-mono text-[11px] tracking-[0.3em] uppercase text-emerald-500 flex items-center gap-2">
          <CheckCircle2 className="w-3.5 h-3.5" /> Show ready
        </div>
        <h1 className="fl-display text-4xl font-extrabold tracking-tighter mt-2">{order.business_name}</h1>
        <div className="text-slate-400 text-sm">{Math.round(order.duration || 0)} seconds · {order.regenerations_max - order.regenerations_used} free re-tries left</div>

        <div className="fl-card p-5 mt-6">
          <div className="flex items-center gap-3">
            <button onClick={togglePlay} className="fl-btn !p-3" data-testid="produce-play">
              {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            </button>
            <a
              href={resolveAudio(order.audio_url)}
              download={`fliboe_show_${order.business_name.replace(/\s+/g, "_")}.mp3`}
              className="fl-btn ghost"
              data-testid="produce-download"
            >
              <Download className="w-4 h-4" /> Download MP3
            </a>
            <button
              onClick={regenerate}
              disabled={submitting || order.regenerations_used >= order.regenerations_max}
              className="fl-btn ghost"
              data-testid="produce-regenerate"
            >
              <RotateCw className={`w-4 h-4 ${submitting ? "animate-spin" : ""}`} /> Re-try
            </button>
          </div>
          <audio
            ref={audioRef}
            src={resolveAudio(order.audio_url)}
            onEnded={() => setPlaying(false)}
            preload="metadata"
            className="hidden"
          />
        </div>

        {/* Script preview */}
        <details className="mt-4">
          <summary className="text-xs fl-mono uppercase tracking-widest text-emerald-500 cursor-pointer">
            Read the script
          </summary>
          <pre className="mt-3 fl-card p-4 text-sm text-slate-300 whitespace-pre-wrap leading-relaxed" data-testid="produce-script">{order.script}</pre>
        </details>

        {order.includes_ad ? (
          <div className="fl-card p-5 mt-6 border-emerald-500/40 bg-emerald-500/5">
            <div className="flex items-start gap-3">
              <Radio className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
              <div>
                <div className="text-white font-medium">Bundle ad slot · pending review</div>
                <div className="text-xs text-slate-400 mt-1">
                  Your show is auto-submitted to air on every Fliboe station for {order.ad_days || 7} days. Admin reviews it within 24 hours, then it's worldwide on-air.
                </div>
              </div>
            </div>
          </div>
        ) : (
          <a
            href="/advertise"
            className="fl-card p-5 mt-6 block hover:bg-white/5 transition"
            data-testid="produce-upsell-ad"
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-white font-medium">Want this on-air for 7 days?</div>
                <div className="text-xs text-slate-400 mt-1">
                  Sponsor a 7-day ad slot for $35-$45 and your show airs every few songs across every Fliboe station.
                </div>
              </div>
              <Sparkles className="w-5 h-5 text-emerald-500" />
            </div>
          </a>
        )}
      </main>
    );
  }

  if (order.status === "generating") {
    return (
      <main className="max-w-xl mx-auto px-5 py-20" data-testid="produce-generating">
        <div className="fl-card p-6 flex items-start gap-3">
          <Loader2 className="w-6 h-6 text-emerald-500 animate-spin shrink-0 mt-1" />
          <div>
            <div className="text-white font-medium">Writing your script + recording the voice…</div>
            <div className="text-xs text-slate-400 mt-1">Usually 20-40 seconds. The page refreshes automatically.</div>
          </div>
        </div>
      </main>
    );
  }

  if (order.status === "error") {
    return (
      <main className="max-w-xl mx-auto px-5 py-20" data-testid="produce-error-state">
        <div className="fl-card p-6 flex items-start gap-3 border-red-500/40">
          <AlertCircle className="w-6 h-6 text-red-400 shrink-0 mt-1" />
          <div>
            <div className="text-white font-medium">Something went wrong</div>
            <div className="text-xs text-slate-400 mt-1">{order.error || "Unknown error"} — please reply to your confirmation email and we'll fix it manually.</div>
          </div>
        </div>
      </main>
    );
  }

  // Wizard form (awaiting_wizard)
  return (
    <main className="max-w-2xl mx-auto px-5 py-14" data-testid="produce-wizard">
      <div className="fl-mono text-[11px] tracking-[0.3em] uppercase text-emerald-500">// 90-second wizard</div>
      <h1 className="fl-display text-4xl font-extrabold tracking-tighter mt-2">Tell us about your show</h1>
      <p className="text-slate-400 text-sm mt-2">{order.business_name} · {order.package_id}</p>

      <div className="space-y-4 mt-8">
        <Field label="1 · Show name" value={answers.show_name} onChange={(v) => setAnswers({ ...answers, show_name: v })} placeholder="e.g. The Money Show, Beats After Dark, Founder Notes" testid="wiz-show-name" />
        <Field label="2 · Guest name" value={answers.guest_name} onChange={(v) => setAnswers({ ...answers, guest_name: v })} placeholder="e.g. Anup" testid="wiz-guest-name" />
        <Field label="3 · Guest title / position" value={answers.guest_title} onChange={(v) => setAnswers({ ...answers, guest_title: v })} placeholder="e.g. founder of TheBudgetBrain" testid="wiz-guest-title" />
        <Field label="4 · Who are you talking to?" value={answers.audience} onChange={(v) => setAnswers({ ...answers, audience: v })} placeholder="e.g. hungry locals, indie artists, fitness beginners" testid="wiz-audience" />
        <Field label="5 · What problem do you solve? (the angle)" value={answers.why} onChange={(v) => setAnswers({ ...answers, why: v })} placeholder="e.g. we cook everything from scratch with local ingredients" testid="wiz-why" multiline />
        <Field label="6 · Your one signature line / catchphrase" value={answers.tagline} onChange={(v) => setAnswers({ ...answers, tagline: v })} placeholder="e.g. Come hungry. Leave happy." testid="wiz-tagline" />
        <Field label="7 · Call to action — what should listeners do?" value={answers.cta} onChange={(v) => setAnswers({ ...answers, cta: v })} placeholder="e.g. Visit us at 7th Avenue or call 555-1234" testid="wiz-cta" />

        <div>
          <label className="text-xs fl-mono uppercase tracking-widest text-slate-400">8 · Voice vibe</label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
            {VIBES.map((v) => (
              <button
                key={v.id}
                onClick={() => setAnswers({ ...answers, vibe: v.id })}
                className={`fl-card p-3 text-left transition ${answers.vibe === v.id ? "ring-2 ring-emerald-500 bg-emerald-500/5" : "hover:bg-white/5"}`}
                data-testid={`wiz-vibe-${v.id}`}
              >
                <div className="text-white text-sm font-semibold">{v.label}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">{v.desc}</div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs fl-mono uppercase tracking-widest text-slate-400">9 · Language</label>
          <p className="text-[11px] text-slate-500 mt-1">The whole show — host and guest — will be written and voiced in this language.</p>
          <select
            className="fl-input mt-2 w-full sm:w-72"
            value={answers.language}
            onChange={(e) => setAnswers({ ...answers, language: e.target.value })}
            data-testid="wiz-language"
          >
            {languages.map(l => (
              <option key={l.key} value={l.key}>{l.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-xs fl-mono uppercase tracking-widest text-slate-400">10 · Voice cast</label>
          <p className="text-[11px] text-slate-500 mt-1">Pick who's asking the questions and who's answering.</p>
          <div className="grid grid-cols-2 gap-3 mt-2 max-w-md">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-slate-500">Host (asks)</span>
              <select
                className="fl-input mt-1 w-full"
                value={answers.host_gender}
                onChange={(e) => setAnswers({ ...answers, host_gender: e.target.value })}
                data-testid="wiz-host-gender"
              >
                <option value="">Pick host voice…</option>
                <option value="female">Female</option>
                <option value="male">Male</option>
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-slate-500">Guest (answers)</span>
              <select
                className="fl-input mt-1 w-full"
                value={answers.guest_gender}
                onChange={(e) => setAnswers({ ...answers, guest_gender: e.target.value })}
                data-testid="wiz-guest-gender"
              >
                <option value="">Pick guest voice…</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </label>
          </div>
        </div>
      </div>

      {err && (
        <div className="mt-4 flex items-start gap-2 text-sm text-red-400" data-testid="wiz-error">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {err}
        </div>
      )}

      <button onClick={submit} disabled={submitting} className="fl-btn mt-6 w-full sm:w-auto" data-testid="wiz-generate">
        <Sparkles className="w-4 h-4" />
        {submitting ? "Generating…" : "Generate my show →"}
      </button>
    </main>
  );
}

function Field({ label, value, onChange, placeholder, testid, multiline }) {
  return (
    <div>
      <label className="text-xs fl-mono uppercase tracking-widest text-slate-400">{label}</label>
      {multiline ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="fl-input mt-1.5 h-20" data-testid={testid} />
      ) : (
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="fl-input mt-1.5" data-testid={testid} />
      )}
    </div>
  );
}
